import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  CircularProgress,
  TextField,
  Chip,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Link as LinkIcon,
  CheckCircle as ConnectedIcon,
  Cancel as DisconnectedIcon,
  Refresh as TestIcon,
  Delete as DisconnectIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { pageVariants, sectionVariants } from '../animations/variants';

const accentColor = '#4698D3';

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Metropolis, sans-serif',
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: '#E5E5E5' },
    '&:hover fieldset': { borderColor: accentColor },
    '&.Mui-focused fieldset': { borderColor: accentColor },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#666666',
    '&.Mui-focused': { color: accentColor },
  },
};

const SaaSConnection = () => {
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const [connected, setConnected] = useState(false);
  const [connection, setConnection] = useState(null);

  const [saasUrl, setSaasUrl] = useState('https://ops.prism.cloudkeeper.com');
  const [code, setCode] = useState('');

  const fetchConnection = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getSaaSConnection();
      if (response.success && response.data) {
        setConnected(!!response.data.connected);
        if (response.data.connected) {
          setConnection(response.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch SaaS connection:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnection();
  }, [fetchConnection]);

  const handleConnect = async () => {
    if (!saasUrl || !code) return;
    setConnecting(true);
    try {
      const response = await apiService.redeemSaaSCode(saasUrl, code);
      if (response.success) {
        toast.success('Connected to CloudKeeper SaaS');
        setCode('');
        fetchConnection();
      } else {
        toast.error(response.message || 'Failed to connect');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to redeem pairing code');
    } finally {
      setConnecting(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await apiService.testSaaSConnection();
      if (response.success) {
        toast.success('Connection is healthy');
        fetchConnection();
      } else {
        toast.error(response.message || 'Connection test failed');
      }
    } catch (error) {
      toast.error(error.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const response = await apiService.disconnectSaaS();
      if (response.success) {
        toast.success('Disconnected from CloudKeeper SaaS');
        setConnected(false);
        setConnection(null);
        setDisconnectOpen(false);
      } else {
        toast.error(response.message || 'Failed to disconnect');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString();
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F9FBFD' }}>
        <CircularProgress sx={{ color: accentColor }} />
      </Box>
    );
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{ minHeight: '100vh', padding: '32px', backgroundColor: '#F9FBFD' }}
    >
      {/* Header */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
        <Paper elevation={0} sx={{ backgroundColor: '#FFFFFF', borderRadius: '8px', border: '1px solid #E5E5E5', p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ backgroundColor: accentColor, width: 48, height: 48 }}>
                <LinkIcon sx={{ fontSize: 24 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, fontSize: '1.75rem', color: '#253E66' }}>
                  CloudKeeper Link
                </Typography>
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#999999', fontWeight: 500 }}>
                  Connect this Prism instance to CloudKeeper SaaS for org-level operations
                </Typography>
              </Box>
            </Box>
            {connected && (
              <Chip
                icon={<ConnectedIcon sx={{ fontSize: 16 }} />}
                label="Connected"
                sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, fontFamily: 'Metropolis, sans-serif' }}
              />
            )}
          </Box>
        </Paper>
      </motion.div>

      {/* Connection Card */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
        <Paper elevation={0} sx={{ backgroundColor: '#FFFFFF', borderRadius: '8px', border: '1px solid #E5E5E5', p: 3 }}>
          {connected ? (
            /* Connected State */
            <>
              <Alert severity="success" sx={{ mb: 3, fontFamily: 'Metropolis, sans-serif' }}>
                This Prism instance is paired with CloudKeeper SaaS.
              </Alert>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 4 }}>
                  <Box>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#999999', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                      Customer Name
                    </Typography>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                      {connection?.customer_name || '-'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 4 }}>
                  <Box>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#999999', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                      Paired At
                    </Typography>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#666666' }}>
                      {formatDate(connection?.paired_at)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#999999', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                      Last Tested
                    </Typography>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#666666' }}>
                      {formatDate(connection?.last_used_at)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={testing ? <CircularProgress size={16} /> : <TestIcon />}
                  onClick={handleTest}
                  disabled={testing}
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    borderColor: accentColor,
                    color: accentColor,
                    '&:hover': { borderColor: accentColor, backgroundColor: 'rgba(70,152,211,0.04)' },
                  }}
                >
                  Test Connection
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DisconnectIcon />}
                  onClick={() => setDisconnectOpen(true)}
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    borderColor: '#E74C3C',
                    color: '#E74C3C',
                    '&:hover': { borderColor: '#C0392B', backgroundColor: 'rgba(231,76,60,0.04)' },
                  }}
                >
                  Disconnect
                </Button>
              </Box>
            </>
          ) : (
            /* Not Connected State */
            <>
              <Alert severity="info" sx={{ mb: 3, fontFamily: 'Metropolis, sans-serif' }}>
                Connect to CloudKeeper SaaS to enable org-level AWS operations. Enter the SaaS URL and the pairing code
                you received from your CloudKeeper administrator.
              </Alert>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <TextField
                  label="Pairing Code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  fullWidth
                  sx={inputStyle}
                  placeholder="PRISM-XXXXXXXX"
                  inputProps={{ style: { fontFamily: 'monospace', letterSpacing: 2 } }}
                />
              </Box>

              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  startIcon={connecting ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
                  onClick={handleConnect}
                  disabled={connecting || !code}
                  sx={{
                    backgroundColor: accentColor,
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    px: 3,
                    '&:hover': { backgroundColor: '#3580B8' },
                  }}
                >
                  Connect
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </motion.div>

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            border: '1px solid #E5E5E5',
            backgroundColor: '#FFFFFF',
            maxWidth: '480px',
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66' }}>
          Disconnect from CloudKeeper SaaS?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666666' }}>
            This will remove the stored deployment token. You will need a new pairing code from
            your CloudKeeper administrator to reconnect.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDisconnectOpen(false)}
            sx={{ fontFamily: 'Metropolis, sans-serif', color: '#999999', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDisconnect}
            disabled={disconnecting}
            sx={{
              backgroundColor: '#E74C3C',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              '&:hover': { backgroundColor: '#C0392B' },
            }}
          >
            {disconnecting ? <CircularProgress size={16} color="inherit" /> : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default SaaSConnection;
